import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import "./UsmRiskSchoolsModal.css";

const API = `${getBackendUrl()}/api`;
const PAGE_SIZE = 10;

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v) => (typeof v === "number" ? `${v}%` : "—");

export const DATA_COMPLETION_MODAL_CONFIG = {
  generated: {
    id: "generated",
    label: "Generated",
    fallbackTitle: "Generated — Schools Below Full Completion",
  },
  pending: {
    id: "pending",
    label: "Pending",
    fallbackTitle: "Pending — Schools With Outstanding Records",
  },
  top5_pending_load: {
    id: "top5_pending_load",
    label: "Top-5 Pending Load",
    fallbackTitle: "Top-5 Pending Load — Schools in Bottleneck Blocks",
  },
  worst_case_closure: {
    id: "worst_case_closure",
    label: "Worst-case Closure",
    fallbackTitle: "Worst-case Closure — Schools With Longest Timeline",
  },
};

const formatMetric = (school, data) => {
  if (data?.metric_format === "count") return fmt(school.metric_pct);
  return pct(school.metric_pct);
};

const AnalyticsDataCompletionSchoolsModal = ({ open, metric, onClose, scopeLabel, districtPct }) => {
  const navigate = useNavigate();
  const { scope } = useScope();
  const config = metric ? DATA_COMPLETION_MODAL_CONFIG[metric] : null;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!open || !metric) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setQuery("");
    setBlockFilter("");
    setSchoolFilter("");
    setPage(1);

    const params = { metric, limit: 2000 };
    if (scope.districtCode) params.district_code = scope.districtCode;
    if (scope.blockCode) params.block_code = scope.blockCode;
    if (scope.udiseCode) params.udise_code = scope.udiseCode;
    if (scope.districtName) params.district_name = scope.districtName;
    if (scope.blockName) params.block_name = scope.blockName;
    if (!scope.districtName && !scope.districtCode) params.district_name = "PUNE";

    axios
      .get(`${API}/analytics/predictions/data-completion-schools`, { params })
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load school list");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    metric,
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
  ]);

  const blockOptions = useMemo(() => {
    const schools = data?.schools || [];
    return [...new Set(schools.map((s) => s.block_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [data?.schools]);

  const schoolOptions = useMemo(() => {
    const schools = data?.schools || [];
    const scoped = blockFilter
      ? schools.filter((s) => String(s.block_name || "") === blockFilter)
      : schools;
    return [...new Set(scoped.map((s) => s.school_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [data?.schools, blockFilter]);

  const filtered = useMemo(() => {
    let schools = data?.schools || [];
    if (blockFilter) {
      schools = schools.filter((s) => String(s.block_name || "") === blockFilter);
    }
    if (schoolFilter) {
      schools = schools.filter((s) => String(s.school_name || "") === schoolFilter);
    }
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter(
      (s) =>
        String(s.school_name || "").toLowerCase().includes(q) ||
        String(s.block_name || "").toLowerCase().includes(q) ||
        String(s.action_needed || "").toLowerCase().includes(q) ||
        String(s.udise_code || "").includes(q),
    );
  }, [data?.schools, blockFilter, schoolFilter, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const metricLabel = data?.metric_label || config?.label || "Metric";
  const title = data?.title || config?.fallbackTitle || "Data Completion Schools";
  const summaryPct =
    typeof districtPct === "number"
      ? districtPct
      : typeof data?.district_pct === "number"
        ? data.district_pct
        : null;

  if (!open || !metric || !config) return null;

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal usm-risk-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="data-completion-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">Data Completion drill-down · {scopeLabel || "District scope"}</p>
            <h3 id="data-completion-modal-title">{title}</h3>
            <p className="usm-risk-modal-sub">
              District baseline:{" "}
              <b>
                {summaryPct != null
                  ? data?.metric_format === "count"
                    ? `${fmt(summaryPct)} weeks`
                    : `${summaryPct}%`
                  : "—"}
              </b>
              {data?.target_blocks?.length ? (
                <>
                  {" "}
                  · Focus blocks: <b>{data.target_blocks.join(", ")}</b>
                </>
              ) : null}
              {data?.description ? <> · {data.description}</> : null}
            </p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="usm-risk-modal-toolbar usm-risk-modal-toolbar-filters">
          <label className="usm-risk-modal-filter">
            <span>Block</span>
            <select
              value={blockFilter}
              onChange={(e) => {
                setBlockFilter(e.target.value);
                setSchoolFilter("");
                setPage(1);
              }}
              disabled={loading || !blockOptions.length}
            >
              <option value="">All blocks</option>
              {blockOptions.map((block) => (
                <option key={block} value={block}>
                  {block}
                </option>
              ))}
            </select>
          </label>
          <label className="usm-risk-modal-filter">
            <span>School</span>
            <select
              value={schoolFilter}
              onChange={(e) => {
                setSchoolFilter(e.target.value);
                setPage(1);
              }}
              disabled={loading || !schoolOptions.length}
            >
              <option value="">All schools</option>
              {schoolOptions.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          </label>
          {data ? (
            <span className="usm-risk-modal-count">
              Showing {fmt(filtered.length)} of {fmt(data.total_schools)} schools
            </span>
          ) : null}
        </div>

        <div className="usm-risk-modal-body">
          {loading ? (
            <div className="usm-risk-modal-loading">Loading schools…</div>
          ) : error ? (
            <div className="usm-risk-modal-error">{error}</div>
          ) : !data?.schools?.length ? (
            <div className="usm-risk-modal-empty">No schools match this metric in the current scope.</div>
          ) : (
            <>
              <div className="usm-risk-modal-body-search">
                <input
                  type="search"
                  className="usm-risk-modal-search"
                  placeholder="Search school, block, action, or UDISE…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              {!pageRows.length ? (
                <div className="usm-risk-modal-empty">No schools match the selected block, school, or search.</div>
              ) : (
                <table className="usm-risk-modal-table usm-risk-modal-table-wide">
                  <thead>
                    <tr>
                      <th>Block</th>
                      <th>School</th>
                      <th className="usm-num">Students</th>
                      <th className="usm-num">Generated</th>
                      <th className="usm-num">Pending</th>
                      <th className="usm-num">{metricLabel}</th>
                      <th>Priority</th>
                      <th>UDISE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((school) => (
                      <tr
                        key={school.udise_code || `${school.school_name}-${school.block_name}`}
                        className="usm-risk-modal-row"
                        onClick={() => school.udise_code && navigate(`/school/${school.udise_code}`)}
                        title={school.udise_code ? `${school.action_needed || ""} — Open school detail` : school.action_needed}
                      >
                        <td>{school.block_name || "—"}</td>
                        <td>{school.school_name || "—"}</td>
                        <td className="usm-num">{fmt(school.total_students)}</td>
                        <td className="usm-num">{fmt(school.total_generated)}</td>
                        <td className="usm-num">{fmt(school.pending)}</td>
                        <td className="usm-num">{formatMetric(school, data)}</td>
                        <td>
                          <span className={`usm-priority-pill ${String(school.priority || "").toLowerCase()}`}>
                            {school.priority || "—"}
                          </span>
                        </td>
                        <td>{school.udise_code || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {!loading && !error && filtered.length > PAGE_SIZE ? (
          <div className="usm-risk-modal-footer">
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default AnalyticsDataCompletionSchoolsModal;
